/**
 * Which step a creative session opens at — ONE implementation, two mounts.
 *
 * WHY THIS IS A SHARED MODULE AND NOT A COPY
 * ==========================================
 * This is the only rule in the Autonomous workflow that lives in the page
 * rather than on the server (`V2_CREATIVE_AUTHORING_SCOPE.md` §3). v1 and v2
 * both run during waves 2b–3, against ONE database. Two implementations of
 * "which step am I on" would be two answers to a question that has one, and the
 * operator would get whichever screen they happened to open. So it moves, it is
 * not rewritten: v1 calls this and so does v2.
 *
 * THE DEFECT THIS FIXES, MEASURED
 * ==============================
 * The old rule ignored the stored step entirely and re-derived a position from
 * OFFER-scoped queries: any production on the offer → open at Prompts, any
 * approved script → Creative Elements, and so on. Every query was
 * `offers/{id}/...` with nothing session-scoped about it.
 *
 * Live, 2026-09-09: **13 offers have productions and more than one session, and
 * the 89 sessions on them all resumed at step 7** — Prompts — no matter where
 * the operator actually was. Session 4 landed on session 1's productions.
 *
 * The server has had the right answer the whole time. All 110 sessions carry
 * `current_step`, spread across steps 0–6, written by the wizard on every
 * navigation. The client wrote it and then refused to read it.
 *
 * So the rule gets SMALLER: read what was stored. The derivation survives only
 * as a fallback for a session that has no stored step, which no session has
 * today but one created outside the wizard would.
 *
 * THREE SOURCES, NAMED (CLAUDE.md rule 10)
 * ========================================
 * The result says WHERE the step came from — "new", "stored", "derived",
 * "derived-incomplete", "fallback" or "fallback-incomplete". "We know where you
 * were", "we worked it out from the offer's history" and "we worked it out from
 * PART of that history because a lookup failed" are three different claims.
 *
 * The `-incomplete` variants exist because a mutation test caught the first
 * version pretending: the ok/failed flag decorated a warning and changed no
 * decision, so flipping failure to success passed every test. A distinction
 * that alters nothing is not a distinction.
 */

export const STEP_PSYCHOLOGY = 0;
export const STEP_ANGLE = 1;
export const STEP_HOOKS = 2;
export const STEP_SCRIPTS = 3;
export const STEP_CTA = 4;
export const STEP_ELEMENTS = 5;
export const STEP_PROMPTS = 6;

/** The seven stages, in order. Indices are load-bearing — see the constants. */
export const STEPS = [
  { id: "psychology", label: "Psychology Map" },
  { id: "angle", label: "Angle" },
  { id: "hooks", label: "Hook" },
  { id: "scripts", label: "Script" },
  { id: "cta", label: "CTA" },
  { id: "elements", label: "Creative Elements" },
  { id: "prompts", label: "Prompts" },
];

export const LAST_STEP = STEPS.length - 1;

/** A stored step is usable only if it is an integer inside the range. */
export function isValidStep(value) {
  return Number.isInteger(value) && value >= 0 && value <= LAST_STEP;
}

/**
 * Resolve the opening step.
 *
 * `fetchers` is injected rather than imported so this module makes no
 * assumptions about the HTTP client and can be tested without one. Each is
 * `() => Promise<array>` and each MAY reject — a rejection is a failure and is
 * reported as one, never as an empty list.
 *
 * Returns `{ step, source, warnings }`.
 */
export async function resolveInitialStep({
  isNewSession = false,
  session = null,
  offer = null,
  fetchers = {},
} = {}) {
  const warnings = [];

  // A new session starts at the beginning. Nothing to look up, and looking
  // anyway is how a fresh session inherits the last one's position.
  if (isNewSession) {
    return { step: STEP_PSYCHOLOGY, source: "new", warnings };
  }

  // THE STORED STEP — the operator's own recorded position.
  const stored = session?.current_step;
  if (isValidStep(stored)) {
    return { step: stored, source: "stored", warnings };
  }
  if (stored !== undefined && stored !== null) {
    warnings.push(
      `The stored step (${JSON.stringify(stored)}) is not a step number. ` +
        `Falling back to deriving one from this offer's history.`
    );
  }

  // DERIVED — offer-scoped, and that is exactly its weakness, so it runs only
  // when there is nothing stored.
  const call = async (name, fn) => {
    if (typeof fn !== "function") return { ok: false, rows: [], missing: true };
    try {
      const rows = await fn();
      return { ok: true, rows: Array.isArray(rows) ? rows : [] };
    } catch (e) {
      // NOT an empty result. The old code caught every one of these into
      // `{data: {productions: []}}`, so a 500 on the productions endpoint
      // looked exactly like an offer with no productions and moved the
      // operator to a different step.
      warnings.push(
        `Could not read ${name} (${e?.message || "request failed"}), so the ` +
          `opening step was decided without it.`
      );
      return { ok: false, rows: [] };
    }
  };

  // `anyFailed` makes the ok/failed distinction LOAD-BEARING. Without it the
  // flag decorated a warning and changed no decision — a mutation flipping
  // failure to success passed every test, because an empty list and a failed
  // list take the same branch. That is the same shape as the bugs this whole
  // sweep is about, in the code written to fix them.
  //
  // A derivation built on a lookup that did not answer is not the same claim as
  // one built on four that did, and the source now says which.
  let anyFailed = false;
  const derived = (step) => ({
    step,
    source: anyFailed ? "derived-incomplete" : "derived",
    warnings,
  });

  const productions = await call("productions", fetchers.productions);
  anyFailed = anyFailed || productions.ok === false;
  if (productions.ok && productions.rows.length > 0) {
    return derived(STEP_PROMPTS);
  }

  const approved = await call("approved scripts", fetchers.approvedScripts);
  anyFailed = anyFailed || approved.ok === false;
  if (approved.ok && approved.rows.length > 0) {
    return derived(STEP_ELEMENTS);
  }

  const scripts = await call("scripts", fetchers.scripts);
  anyFailed = anyFailed || scripts.ok === false;
  if (scripts.ok && scripts.rows.length > 0) {
    return derived(STEP_SCRIPTS);
  }

  const hooks = await call("hooks", fetchers.hooks);
  anyFailed = anyFailed || hooks.ok === false;
  if (hooks.ok && hooks.rows.some((h) => h && h.selected)) {
    return derived(STEP_SCRIPTS);
  }

  // FALLBACK — the two offer flags, which is all that is left.
  const fallbackSource = anyFailed ? "fallback-incomplete" : "fallback";
  if (offer?.psychology_map_confirmed) {
    return {
      step: offer?.selected_angle_id ? STEP_HOOKS : STEP_ANGLE,
      source: fallbackSource,
      warnings,
    };
  }
  return { step: STEP_PSYCHOLOGY, source: fallbackSource, warnings };
}
